import useLiveSanityContent, {
  LiveSanityContentResponse,
  UseLiveSanityContentResponse,
  sanityLocalized
} from "src/ssc-api/useLiveSanityContent";
import { PartialSanityImageType } from "@silversea-ssc/ui-components-react";
import { useLanguage } from "src/utils/localization/languages/I18nContext";

export type LastSeenProductsContent = {
  cruiseCode: string;
  cardImage: PartialSanityImageType;
  metadata: {
    routing: {
      path: { localized: string };
    };
  };
  data: {
    destination: {
      destinationWebCode: string;
      name: { localized: string };
    };
    departureDate: string;
    departurePort: {
      name: { localized: string };
    };
    arrivalDate: string;
    arrivalPort: {
      name: { localized: string };
    };
    days: number;
  };
};

export type LastSeenProductsContents = readonly LastSeenProductsContent[];

type QueryResult = {
  cruises: LastSeenProductsContents;
  specialCruises: LastSeenProductsContents;
};

export const useLastSeenProductsContent = (
  cruiseCodes?: string[]
): UseLiveSanityContentResponse<LastSeenProductsContents> => {
  const language = useLanguage();
  const quotedCruiseCodes = cruiseCodes ? cruiseCodes.map((x) => `"${x}"`) : [];
  const query = cruiseCodes
    ? `
{
  "cruises": *[_type == "cruise" && !(_id in path("drafts.*")) && data->cruiseCode in [${quotedCruiseCodes}] ] {
    cruiseCode,
    cardImage {
      asset -> {
        _id,
      }
    },
    metadata {
      routing {
        ${sanityLocalized`path`}
      }
    },
    data -> {
      days,
      destination -> {
        destinationWebCode,
        ${sanityLocalized`name`}
      },
      departureDate,
      departurePort -> {
        ${sanityLocalized`name`}
      },
      arrivalDate,
      arrivalPort -> {
        ${sanityLocalized`name`}
      },
    }
  },
  "specialCruises": *[_type == "specialVoyage" && !(_id in path("drafts.*"))  && data->cruiseCode in [${quotedCruiseCodes}] ] {
    "cruiseCode": data->cruiseCode,
    "cardImage": card.image {
      asset -> {
        _id,
      }
    },
    metadata {
      routing {
        ${sanityLocalized`path`}
      }
    },
    data -> {
      days,
      "destination": {
        "name": select(
          cruiseGroup == "GrandVoyage" => *[_id == "site-silversea-com"][0]{
            grandVoyagesPage->{
              ${sanityLocalized`title`}
            }
          }.grandVoyagesPage.title,
          cruiseGroup == "WorldCruise" => *[_id == "site-silversea-com"][0]{
            worldCruisesPage->{
              ${sanityLocalized`title`}
            }
          }.worldCruisesPage.title,
          null
        )
      },
      departureDate,
      departurePort -> {
        ${sanityLocalized`name`}
      },
      arrivalDate,
      arrivalPort -> {
        ${sanityLocalized`name`}
      },
    }
  },
}  `
    : undefined;

  const { isLoading, isError, status, data } =
    useLiveSanityContent<QueryResult>(query, { $lang: language.sanityCode });
  return {
    isLoading,
    isError,
    status,
    data: toArraResult(data)
  };
};

const toArraResult = (data?: LiveSanityContentResponse<QueryResult>) =>
  data
    ? {
        result: [
          ...(data.result?.cruises ?? []),
          ...(data.result?.specialCruises ?? [])
        ],
        query: data.query
      }
    : undefined;
